import * as Yup from "yup";
import { format } from "date-fns";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { useEffect, useState } from "react";
import styles from "../ReportIssues.module.scss";
import Button from "../../../../../common/Button/Button";
import Upload from "../../../../../common/Formik/Upload/Upload";
import Select from "../../../../../common/Formik/Select/Select";
import Textarea from "../../../../../common/Formik/Textarea/Textarea";
import ErrorText from "../../../../../common/Formik/ErrorText/ErrorText";
import InputFormik from "../../../../../common/Formik/Input/InputFormik";
import { cardDetailsAction, reportIssueAction } from "../../../../../../features/user/userSlice";
import CommonDatePicker from "../../../../../common/Formik/DatePiker/DatePicker";

const blobToBase64 = (blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

const CardLost = () => {
  const dispatch = useDispatch()
  const [cardOptions, setCardOptions] = useState([])
  const [loading, setLoading] = useState(false);

  // ********** GET CARD DETAILS **********
  const getCardDetails = async () => {
    try {
      const res = await dispatch(cardDetailsAction());
      if (res?.status === 200) {
        const cards = res?.data?.data || [];
        const options = cards.map((card) => ({
          value: card.rain_card_id,
          label: `**** **** **** ${card.last_four}`,
        }));
        setCardOptions(options)
      } else {
        setCardOptions([])
      }
    } catch (error) {
      console.log(error, "error");
    }
  }

  useEffect(() => {
    getCardDetails()
  }, [])

  const MAX_FILE_SIZE_MB = 2;
  const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
  const SUPPORTED_FORMATS = [
    "image/jpg",
    "image/jpeg",
    "image/png",
    "application/pdf",
  ];

  const validationSchema = Yup.object({
    SelectCard: Yup.object()
      .nullable()
      .required("Please select a card"),

    date: Yup.date()
      .typeError("Invalid date")
      .nullable()
      .required("Last used date is required")
      .max(new Date(), "Date cannot be in the future"),

    LastUsedLocation: Yup.string()
      .trim()
      .required("Last used location is required")
      .min(3, "Location must be at least 3 characters")
      .max(100, "Location must not exceed 100 characters"),

    PoliceReportNumber: Yup.string()
      .trim()
      .matches(
        /^[a-zA-Z0-9-\/]*$/,
        "Only letters, numbers, - and / are allowed"
      )
      .max(50)
      .nullable(),

    description: Yup.string()
      .trim()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters")
      .max(500, "Description must not exceed 500 characters"),


    profile_img: Yup.mixed()
      .nullable()
      .test(
        "fileExists",
        "Invalid file",
        (file) => !file || file instanceof File
      )

      //  Size validation
      .test(
        "fileSize",
        `File size must be less than ${MAX_FILE_SIZE_MB} MB`,
        (file) => !file || file.size <= MAX_FILE_SIZE
      )

      //  Type validation (MIME)
      .test(
        "fileType",
        "Only JPG, JPEG, PNG, or PDF files are allowed",
        (file) => !file || SUPPORTED_FORMATS.includes(file.type)
      )

      //  Extension safety (extra layer)
      .test(
        "fileExtension",
        "Invalid file extension",
        (file) => {
          if (!file) return true;
          const ext = file.name?.split(".").pop()?.toLowerCase();
          return ["jpg", "jpeg", "png", "pdf"].includes(ext);
        }
      ),
  });
  const reportSubmit = async (values, resetForm) => {
    try {
      setLoading(true);

      let attachments;

      if (values.profile_img) {
        const base64 = await blobToBase64(values.profile_img);
        attachments = [base64];
      }

      const payload = {
        dispute_type: "card_lost",
        description: values?.description,
        card_id: values?.SelectCard?.value,
        last_used_location: values?.LastUsedLocation,
        police_report_number: values?.PoliceReportNumber,
        last_used_date: format(new Date(values?.date), "yyyy-MM-dd"),
        ...(attachments && { attachments }),
      };

      const res = await dispatch(reportIssueAction(payload));

      if (res?.status === 200 || res?.status === 201) {
        resetForm();
      }
    } catch (error) {
      console.log(error, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.report_issues_content}>
      <h5>Report Lost Card</h5>

      <div className={styles.report_issues_content_form}>
        <Formik
          initialValues={{
            SelectCard: "",
            date: null,
            LastUsedLocation: "",
            PoliceReportNumber: "",
            description: "",
            profile_img: null,
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm }) => {
            reportSubmit(values, resetForm)
          }}
        >
          {(formik) => (
            <Form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <Row>
                <Col lg={4} md={6}>
                  <Select
                    options={cardOptions}
                    name="SelectCard"
                    label="Select Card"
                    placeholder="**** **** **** 1234"
                    className={styles.input}
                    formik={formik}
                    value={formik.values.SelectCard}
                    onChange={(value) =>
                      formik.setFieldValue("SelectCard", value)
                    }
                  />
                </Col>
                <Col lg={4} md={6}>
                  <CommonDatePicker
                    label="Last Used Date"
                    name="date"
                    value={formik.values.date}
                    placeholder="DD/MM/YYYY"
                    onChange={(date) => formik.setFieldValue("date", date)}
                    onBlur={formik.handleBlur}
                    maxDate={new Date()}
                    formik={formik}
                  />
                </Col>
                <Col lg={4} md={6}>
                  <InputFormik
                    name="LastUsedLocation"
                    label="Last Used Location"
                    placeholder="City, Country"
                    className={styles.input}
                    {...formik.getFieldProps("LastUsedLocation")}
                    formik={formik}
                  />
                </Col>
                <Col lg={4} md={6}>
                  <InputFormik
                    name="PoliceReportNumber"
                    label="Police Report Number"
                    placeholder="Enter If filed With police"
                    className={styles.input}
                    {...formik.getFieldProps("PoliceReportNumber")}
                    formik={formik}
                  />
                </Col>
                <Col lg={4}>
                  <Textarea
                    name="description"
                    label="Description"
                    placeholder="Describe your issue"
                    formik={formik}
                    value={formik.values.description}
                    className={styles.form_textarea}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Col>
                <Col lg={4}>
                  <Upload
                    name="profile_img"
                    label="Upload receipt"
                    formik={formik}
                    image={formik.values.profile_img}
                    setBlobImage={(file) => {
                      formik.setFieldValue("profile_img", file);
                      formik.setFieldTouched("profile_img", true);
                    }}
                  />

                  {formik.touched.profile_img && formik.errors.profile_img && (
                    <ErrorText>{formik.errors.profile_img}</ErrorText>
                  )}
                </Col>
              </Row>
              <div className={styles.form_btns}>
                <Button
                  type="button"
                  className={styles.form_btns_btn}
                  variant="bordered_grey"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className={styles.form_btns_btn}
                  disabled={loading}
                  onClick={(e) => {
                    formik.handleSubmit(e);
                  }}
                >
                  {loading ? "Submitting..." : "Submit Issue"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CardLost;
